import { useAuth } from "./AuthProvider";
import { useState, useEffect, createContext, useContext, useCallback, useMemo } from "react";
import { getUserPrivileges } from "./api/apiClient";

// Create the context
const AccessControlContext = createContext();

// Custom hook to use the access control context
export const useAccessControl = () => {
  const context = useContext(AccessControlContext);
  if (!context) {
    throw new Error('useAccessControl must be used within an AccessControlProvider');
  }
  return context;
};

// Provider component
const AccessControlProvider = ({ children }) => {
  const { currentUser } = useAuth();
  const [privileges, setPrivileges] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [initialized, setInitialized] = useState(false);

  // Function to load privileges from localStorage
  const loadFromCache = () => {
    try {
      const cached = localStorage.getItem('userPrivileges');
      if (cached) {
        const parsed = JSON.parse(cached);
        if (parsed && typeof parsed === 'object') {
          return parsed;
        }
      }
    } catch (error) {
      console.error('Error loading privileges from cache:', error);
    }
    return null;
  };

  // Function to save privileges to localStorage
  const saveToCache = (privileges) => {
    try {
      localStorage.setItem('userPrivileges', JSON.stringify(privileges));
    } catch (error) {
      console.error('Error saving privileges to cache:', error);
    }
  };

  // Function to clear cache
  const clearCache = () => {
    try {
      localStorage.removeItem('userPrivileges');
    } catch (error) {
      console.error('Error clearing privileges cache:', error);
    }
  };

  // Function to fetch privileges from API
  const fetchFromAPI = async (retries = 3, delayMs = 1000) => {
    const headers = {
      "Authorization": `Bearer ${await currentUser.getIdToken()}`,
      "X-User-Id": currentUser.uid,
    };

    for (let attempt = 0; attempt < retries; attempt++) {
      try {
        const response = await getUserPrivileges(headers);
        const data = response.data || {};
        
        // Check if we got valid privileges (has the privileges object)
        if (data && data.privileges && Object.keys(data.privileges).length > 0) {
          return data;
        }
        
        // If empty privileges and not last attempt, wait and retry
        if (attempt < retries - 1) {
          console.log(`Privileges empty, retrying... (attempt ${attempt + 1}/${retries})`);
          await new Promise(resolve => setTimeout(resolve, delayMs));
        } else {
          // Last attempt, return whatever we got
          return data;
        }
      } catch (error) {
        // If error and not last attempt, wait and retry
        if (attempt < retries - 1) {
          console.log(`Error fetching privileges, retrying... (attempt ${attempt + 1}/${retries})`, error);
          await new Promise(resolve => setTimeout(resolve, delayMs));
        } else {
          // Last attempt, throw the error
          throw error;
        }
      }
    }
    
    return {};
  };

  // Function to initialize privileges
  const initializePrivileges = async () => {
    if (!currentUser) {
      setPrivileges({});
      setLoading(false);
      setInitialized(true);
      clearCache();
      return;
    }

    // Check cache first
    const cachedPrivileges = loadFromCache();
    if (cachedPrivileges) {
      // Use cached data immediately
      setPrivileges(cachedPrivileges);
      setLoading(false);
      setInitialized(true);

      // Refresh from API in background
      try {
        const freshPrivileges = await fetchFromAPI();
        setPrivileges(freshPrivileges);
        saveToCache(freshPrivileges);
      } catch (error) {
        console.error('Failed to refresh privileges from API:', error);
        // Keep cached data on refresh failure
      }
    } else {
      // No cache, fetch from API
      try {
        setLoading(true);
        setError(null);
        const freshPrivileges = await fetchFromAPI();
        setPrivileges(freshPrivileges);
        saveToCache(freshPrivileges);
        setLoading(false);
        setInitialized(true);
      } catch (err) {
        console.error('Failed to fetch privileges from API:', err);
        setError(err);
        setPrivileges({});
        setLoading(false);
        setInitialized(true);
      }
    }
  };

  // Initialize on mount and when user changes
  useEffect(() => {
    initializePrivileges();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser]);

  // Function to update privileges in cache
  const updatePrivileges = useCallback((newPrivileges) => {
    const safePrivileges = newPrivileges || {};
    setPrivileges(safePrivileges);
    saveToCache(safePrivileges);
  }, []);

  // Function to refresh privileges from server
  const refreshPrivileges = useCallback(async () => {
    if (!currentUser) return;

    try {
      const freshPrivileges = await fetchFromAPI();
      setPrivileges(freshPrivileges);
      saveToCache(freshPrivileges);
    } catch (error) {
      console.error('Failed to refresh privileges:', error);
      throw error; // Re-throw so caller can handle
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser]);

  // Function to clear cache manually
  const clearPrivilegesCache = useCallback(() => {
    clearCache();
    setPrivileges({});
  }, []);

  // Fetch privileges when user changes
//   useEffect(() => {
//     fetchPrivileges();
//   }, [currentUser]);

  const value = useMemo(() => ({
    privileges,
    loading,
    error,
    initialized,
    updatePrivileges,
    refreshPrivileges,
    clearPrivilegesCache,
  }), [privileges, loading, error, initialized, updatePrivileges, refreshPrivileges, clearPrivilegesCache]);

  return (
    <AccessControlContext.Provider value={value}>
      {children}
    </AccessControlContext.Provider>
  );
};

export default AccessControlProvider;